import { falAngleLeft } from 'shared/@generated/icons/falAngleLeft';
import { falBars } from 'shared/@generated/icons/falBars';
import { falCartShopping } from 'shared/@generated/icons/falCartShopping';
import { falHeart } from 'shared/@generated/icons/falHeart';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, View } from 'react-native';
import logo from 'shared/assets/logo.svg';
import { BaseModalRef } from 'shared/components/base-modal';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { Icon } from 'shared/components/icon';
import { Link } from 'shared/components/link';
import { SvgImage } from 'shared/components/svg-image';
import { Text } from 'shared/components/text';
import { useIsCurrentRoute, useRoute } from 'shared/library/routes/route';
import useShopStore from 'shared/state/shop';
import { useScrolled } from 'shared/library/use-scrolled';
import { MenuFooter } from './menu';
import { Button } from 'shared/components/button';
import { useRouter } from 'shared/library/use-router';
import useApi from 'shared/library/use-api';
import { Order } from 'shared/@generated/models';
import { apiRoute } from 'shared/library/api-route';
import { useLocalStorage } from 'shared/library/use-local-storage';
import { useFormatter } from 'shared/library/use-formatter';

export function ShopMenu() {
  const { t } = useTranslation('web');
  const route = useRoute();
  const router = useRouter();
  const isCurrentRoute = useIsCurrentRoute();
  const categoriesMenuRef = useRef<BaseModalRef>(null);
  const settings = useShopStore((state) => state.settings);
  const categories = useShopStore((state) => state.productCategories);
  const { formatCurrency } = useFormatter();
  const { scrolled } = useScrolled();

  return (
    <>
      <div className={'h-[var(--header-h)]'} />

      <nav className={'bg-brand-green-dark fixed top-0 z-50 h-[var(--header-h)] w-full'}>
        <div
          className={'bg-brand-green-dark justify-center px-3'}
          style={{ height: 'calc(var(--header-h) - var(--header-menu-h))' } as any}
        >
          <div className={'mx-auto flex h-full w-full max-w-screen-xl items-center justify-center'}>
            <div className={'absolute left-0 hidden md:flex'}>
              <Link href={route('home')} asBlock={true} asInline={true}>
                <View className={'flex-row items-center'}>
                  <Icon icon={falAngleLeft} className={'text-brand-cream-light size-4'} />
                  <Text
                    className={'text-brand-cream-light text-sm font-semibold group-hover:underline'}
                  >
                    {t('shop.shop_menu.back_to_marketplace')}
                  </Text>
                </View>
              </Link>
            </div>

            {settings && (
              <>
                <Text
                  className={'text-brand-cream-light text-center text-xs font-semibold md:text-sm'}
                >
                  {t('shop.shop_menu.delivery', {
                    deliveryMin: settings.shipping_days_min,
                    deliveryMax: settings.shipping_days_max,
                  })}
                </Text>
                <Text className={'text-brand-cream-light mx-1.5'}>·</Text>
                <Text
                  className={'text-brand-cream-light text-center text-xs font-semibold md:text-sm'}
                >
                  {t('shop.shop_menu.free_shipping', {
                    freeShippingThreshold: formatCurrency(settings.free_shipping_from_minor / 100),
                  })}
                </Text>
              </>
            )}
          </div>
        </div>

        <div
          className={`bg-brand-cream-light h-[var(--header-menu-h)] border-b px-3 transition-colors duration-75 md:rounded-t-xl ${scrolled ? 'border-brand-brown-dark/10' : 'border-transparent'}`}
        >
          <div
            className={'mx-auto flex h-full w-full max-w-screen-xl items-center justify-between'}
          >
            <div className={'flex items-center gap-3'}>
              <FullscreenModal
                ref={categoriesMenuRef}
                headerTitle={t('shop.shop_menu.menu')}
                trigger={(modal) => (
                  <TouchableOpacity className={'rounded p-2 md:hidden'} onPress={modal.open}>
                    <Icon icon={falBars} className={'text-brand-brown-dark size-6'} />
                  </TouchableOpacity>
                )}
              >
                <View className={'-m-3'}>
                  <Link
                    href={route('shop.home')}
                    asBlock={true}
                    containerClassName={`border-brand-brown-dark/10 w-full border-b px-4 py-3 ${
                      isCurrentRoute('shop.home') ? 'bg-brand-cream' : ''
                    }`}
                    onPress={() => categoriesMenuRef.current?.close()}
                  >
                    <Text className={'text-brand-brown-dark'}>
                      {t('shop.shop_menu.all_products')}
                    </Text>
                  </Link>

                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      href={route('shop.category', { category: category.slug })}
                      asBlock={true}
                      containerClassName={`border-brand-brown-dark/10 w-full border-b px-4 py-3 ${
                        isCurrentRoute('shop.category', { category: category.slug })
                          ? 'bg-brand-cream'
                          : ''
                      }`}
                      onPress={() => categoriesMenuRef.current?.close()}
                    >
                      <Text className={'text-brand-brown-dark'}>{category.name}</Text>
                    </Link>
                  ))}

                  <Button
                    onPress={() => {
                      categoriesMenuRef.current?.close();
                      router.push(route('home'));
                    }}
                    text={t('shop.shop_menu.back_to_marketplace')}
                    icon={falAngleLeft}
                    iconPosition={'left'}
                    color={'secondary'}
                    className={'mt-3 self-center'}
                  />
                </View>

                <MenuFooter />
              </FullscreenModal>

              <Link href={route('shop.home')} asBlock={true}>
                <SvgImage
                  src={logo}
                  alt={'Get a Pet Logo'}
                  containerClassName={'aspect-logo h-8'}
                />
              </Link>
            </div>

            <div className={'hidden flex-1 flex-wrap items-center justify-center gap-1 md:flex'}>
              {categories.map((category, i) => (
                <Link
                  key={i}
                  href={route('shop.category', { category: category.slug })}
                  textClassName={`px-3 py-2 text-sm md:text-base ${isCurrentRoute('shop.category', { category: category.slug }) ? 'text-brand-brown-dark underline' : 'text-brand-brown'}`}
                >
                  {category.name}
                </Link>
              ))}
            </div>

            <div className={'flex items-center gap-1 md:gap-2'}>
              {/*<Link*/}
              {/*  href={route('shop.home')}*/}
              {/*  asBlock={true}*/}
              {/*  containerClassName={'rounded px-2 py-1'}*/}
              {/*>*/}
              {/*  <View className={'items-center'}>*/}
              {/*    <Icon icon={falSearch} className={'text-brand-brown-dark'} />*/}
              {/*    <Text*/}
              {/*      className={'text-brand-brown-dark text-2xs group-hover:underline md:text-xs'}*/}
              {/*    >*/}
              {/*      {t('shop.shop_menu.search')}*/}
              {/*    </Text>*/}
              {/*  </View>*/}
              {/*</Link>*/}

              <Link
                href={route('shop.likes')}
                asBlock={true}
                containerClassName={'rounded px-2 py-1'}
              >
                <View className={'items-center'}>
                  <Icon icon={falHeart} className={'text-brand-brown-dark'} />
                  <Text
                    className={'text-brand-brown-dark text-2xs group-hover:underline md:text-xs'}
                  >
                    {t('shop.shop_menu.likes')}
                  </Text>
                </View>
              </Link>

              <CartButton />
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

function CartButton() {
  const { t } = useTranslation('web');
  const route = useRoute();
  const api = useApi();
  const { value, remove } = useLocalStorage<string>('order_uuid');
  const orderItemsCount = useShopStore((state) => state.order?.order_items?.length ?? 0);
  const setOrder = useShopStore((state) => state.setOrder);

  function loadOrder(orderUuid: string) {
    api
      .request<{ order: Order }>(apiRoute('orders.show', { order: orderUuid }))
      .then((response) => {
        setOrder(response.order);
      })
      .catch(() => {
        remove();
      });
  }

  useEffect(() => {
    if (value) {
      loadOrder(value);
    }
  }, [value]);

  return (
    <Link href={route('shop.checkout')} asBlock={true} containerClassName={'rounded px-2 py-1'}>
      <View className={'relative items-center'}>
        <Icon icon={falCartShopping} className={'text-brand-brown-dark'} />
        <Text className={'text-brand-brown-dark text-2xs group-hover:underline md:text-xs'}>
          {t('shop.shop_menu.checkout')}
        </Text>

        <View
          className={
            'bg-brand-brown-dark border-brand-cream-light absolute -right-1 -top-1 size-5 items-center justify-center rounded-full border-2'
          }
        >
          <Text className={'text-2xs text-center font-black text-white'}>{orderItemsCount}</Text>
        </View>
      </View>
    </Link>
  );
}
